import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import CustomerHistologyWaitSection from "./CustomerHistologyWaitSection";
import CustomerReviewCutoffSection from "./CustomerReviewCutoffSection";
import ReviewQueuePrioritySection from "./ReviewQueuePrioritySection";

interface Customer {
  id: string;
  label: string;
  rules: string | null;
  created_at: string;
  updated_at: string;
}

export default function CustomerManagementPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const customerId = searchParams.get("customerId");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(customerId || "");
  const [label, setLabel] = useState("");
  const [rules, setRules] = useState("");

  // Fetch all customers
  const { data: customers, isLoading: customersLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase.from("customers").select("*").order("label");

      if (error) throw error;
      return data as Customer[];
    },
  });

  // Fetch selected customer details
  const { data: selectedCustomer, isLoading: customerLoading } = useQuery({
    queryKey: ["customer", selectedCustomerId],
    queryFn: async () => {
      if (!selectedCustomerId) return null;

      const { data, error } = await supabase.from("customers").select("*").eq("id", selectedCustomerId).maybeSingle();

      if (error) throw error;
      return data as Customer | null;
    },
    enabled: !!selectedCustomerId,
  });

  // Update form when selected customer changes
  useEffect(() => {
    if (selectedCustomer) {
      setLabel(selectedCustomer.label || "");
      setRules(selectedCustomer.rules || "");
    }
  }, [selectedCustomer]);

  // Sync URL params with selected customer
  useEffect(() => {
    const urlCustomerId = searchParams.get("customerId");
    if (urlCustomerId && urlCustomerId !== selectedCustomerId) {
      setSelectedCustomerId(urlCustomerId);
    }
  }, [searchParams, selectedCustomerId]);

  // Update mutation
  const updateCustomerMutation = useMutation({
    mutationFn: async ({ id, label, rules }: { id: string; label: string; rules: string }) => {
      const { data, error } = await supabase.from("customers").update({ label, rules }).eq("id", id).select().single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customer", selectedCustomerId] });
      toast.success("Customer updated successfully");
    },
    onError: (error) => {
      toast.error(`Failed to update customer: ${error.message}`);
    },
  });

  const handleCustomerChange = (value: string) => {
    setSelectedCustomerId(value);
    navigate(`?customerId=${value}`, { replace: true });
  };

  const handleSave = () => {
    if (!selectedCustomerId) {
      toast.error("Please select a customer");
      return;
    }

    if (!label?.trim()) {
      toast.error("Label cannot be empty");
      return;
    }

    updateCustomerMutation.mutate({
      id: selectedCustomerId,
      label: label.trim(),
      rules: rules?.trim() || "",
    });
  };

  const isFormDisabled = !selectedCustomerId || customerLoading;
  const isSaving = updateCustomerMutation.isPending;

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <h1 className="text-3xl font-bold mb-6">Customer Management</h1>

      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="customer-select">Select Customer</Label>
          <Select value={selectedCustomerId} onValueChange={handleCustomerChange} disabled={customersLoading}>
            <SelectTrigger id="customer-select" className="w-full">
              <SelectValue placeholder="Choose a customer..." />
            </SelectTrigger>
            <SelectContent>
              {customers?.map((customer) => (
                <SelectItem key={customer.id} value={customer.id}>
                  {customer.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedCustomerId && (
          <div className="space-y-6 border rounded-lg p-6 bg-card">
            <div className="space-y-2">
              <Label htmlFor="customer-label">Customer Label</Label>
              <Input
                id="customer-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                disabled={isFormDisabled}
                placeholder="Enter customer name..."
              />
            </div>

            <div className="space-y-2">
              <MarkdownEditor
                value={rules}
                onChange={setRules}
                label="Rules"
                placeholder="Enter customer rules in markdown format..."
                isFullScreen={false}
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={isFormDisabled || isSaving}>
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        )}

        {!selectedCustomerId && !customersLoading && (
          <div className="text-center py-12 text-muted-foreground">
            Please select a customer to view and edit their details
          </div>
        )}

        <ReviewQueuePrioritySection />
        <CustomerReviewCutoffSection />
        <CustomerHistologyWaitSection />
      </div>
    </div>
  );
}
